import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"
import { format, parseISO } from "date-fns"; // For date handling

/**
 * Utility function for conditionally joining Tailwind CSS class names.
 * It uses clsx for conditional grouping and twMerge for resolving conflicts.
 * @param {import("clsx").ClassValue[]} inputs - Array of class names or class objects.
 * @returns {string} The merged class string.
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// --- API Configuration ---

/**
 * The base URL for the backend API.
 * Uses the environment variable REACT_APP_API_BASE_URL if available, otherwise defaults to localhost.
 * Note: The /api prefix is already included here to simplify usage throughout the application.
 */
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';


// --- Formatting Utilities ---

/**
 * Formats a number as USD currency
 * @param {number} amount - The numeric amount to format.
 * @returns {string} The formatted currency string (e.g., "$1,234.56").
 */
export function formatCurrency(amount) {
    // Ensure the amount is a valid number
    const numericAmount = typeof amount === 'number' ? amount : parseFloat(amount);
    if (isNaN(numericAmount)) return "$0.00";

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(numericAmount);
}

/**
 * Formats a raw date string (e.g., ISO format) into a readable date format.
 * @param {string} dateString - The ISO date string from the backend.
 * @param {string} dateFormat - The format string (e.g., 'MMM dd, yyyy'). Defaults to 'MMM dd, yyyy'.
 * @returns {string} The formatted date string.
 */
export function formatDate(dateString, dateFormat = 'MMM dd, yyyy') {
    if (!dateString) return 'N/A';
    try {
        // parseISO handles ISO 8601 strings correctly
        const date = parseISO(dateString);
        return format(date, dateFormat);
    } catch (error) {
        console.error("Error formating date:", error);
        return dateString;
    }
}

/**
 * Formats a number as a percentage.
 * @param {number} value - The numeric value (e.g., 0.05 for 5%).
 * @returns {string} The formatted percentage string (e.g., "5.00%").
 */
export function formatPercentage(value){
    // Ensure the value is a valid number
    const numericValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numericValue)) return "0%";

    return new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(numericValue);
}

// --- Timezone Utilities ---

/**
 * Common timezones for selection UI.
 */

// --- Code goes here ---

/**
 * Converts a Date object from the assumed local timezone to a UTC ISO string,
 * compensating for the user's selected timezone offset.
 * This is the fix: It ensures the clock time the user entered (which created the 'date' object)
 * is correctly interpreted as being in their 'timezone' preference, and then converts it to the correct UTC timestamp.
 * @param {Date | string} date - The local Date object (or date string) created from user input.
 * @param {string} timezone - The IANA timezone string (e.g., "America/New York")
 * @returns {string|null} The date in ISO 8601 UTC format (e.g., 'YYYY-MM-DDTHH:MM:SSZ')
 */
export function toUTC(date, timezone) {
    if (!date) return null;
    // Fallback if no timezone is provided (will use browser's default offset)
    if (!timezone) {
        return new Date(date).toISOString();
    }

    const localDate = new Date(date);

    // 1. Use toLocaleString to get the date and time components (Year, Month, Day, Hour, Minute)
    //    as they would appear on the clock in the user's selected 'timezone'.
    //    We use 'en-CA' as the local because it produces a reliable 'YYYY-MM-DD, HH:MM:SS' format.
    const zonedDateTimeStr = localDate.toLocaleDateString('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: timezone, // Crucial: Apply the target timezone for formatting
    });

    // 2. Format the string into an unambiguous 'YYYY-MM-DDTHH:MM:SS' string.
    //    e.g., "2024-05-28, 10:00:00" -> "2024-05-28T10:00:00"
    const isoStringNaive = zonedDateTimeStr.replace(/, /g, 'T');

    // 3. Create a new Date object using this naive string, but explicitly appending the
    //    IANA timezone name. This tells modern JavaScript how to correctly calculate the
    //    UTC offset for that specific date/time (handling DST correctly).
    const timezoneAwareString = `${isoStringNaive} ${timezone}`;
    const targetDate = new Date(timezoneAwareString);

    // 4. Return the UTC ISO string.
    if (isNaN(targetDate.getTime())) {
        console.warn("IANA timezone parsing failed in new Date(), falling back to naive string conversation.");
        // Fallback: Use standard ISO parsing if IANA string fails (less reliable, but safe)
        return new Date(isoStringNaive).toISOString();
    }

    return targetDate.toISOString();
}

/**
 * Converts a UTC ISO string to a display string based on the user's selected timezone.
 * * NOTE: This function has been updated to use native JS Intl.DateTimeFormat (toLocaleString)
 * for reliable timezone display, as date-fns/format does not natively support IANA timezones.
 * * @param {string} utcString - The UTC ISO 8601 string (e.g., '2024-06-01T10:00:00Z')
 * @param {string} timezone - The IANA timezone string (e.g., "America/New_York")
 * @returns {string} The formatted local time string.
 */
export function formatTimezone(utcString, timezone) {
    if (!utcString || !timezone) {
        // Fallback if UTC string or timezone is missing
        return utcString ? format(new Date(utcString), 'MMM dd, yyyy h:mm a') : 'N/A';
    }

    const date = new Date(utcString);

    return date.toLocaleString('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true, // Use AM/PM format
    });
}
